import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatINR, formatDate, numberToWords } from "../lib/format";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Printer, X } from "lucide-react";

export function ReceiptDialog({ id, open, onClose }) {
  const [data, setData] = useState(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    if (id && open) { setData(null); api.get(`/receipts/${id}`).then((r) => setData(r.data)).catch(() => {}); }
  }, [id, open]);

  if (!open) return null;
  const txn = data?.txn;
  const cand = data?.candidate;
  const st = data?.settings || {};
  const amount = txn ? (txn.money_in || txn.amount) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="receipt-dialog">
        <div className="flex items-center justify-between px-4 py-3 border-b no-print">
          <div className="flex gap-2">
            <button onClick={() => setCompact(false)} data-testid="receipt-a4" className={`text-xs px-3 py-1.5 rounded-lg ${!compact ? "bg-[#0F2942] text-white" : "bg-slate-100 text-slate-600"}`}>A4 Receipt</button>
            <button onClick={() => setCompact(true)} data-testid="receipt-compact" className={`text-xs px-3 py-1.5 rounded-lg ${compact ? "bg-[#0F2942] text-white" : "bg-slate-100 text-slate-600"}`}>Compact</button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => window.print()} data-testid="receipt-print" className="bg-teal-600 hover:bg-teal-700 gap-1.5"><Printer className="h-3.5 w-3.5" /> Print / PDF</Button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {!data ? <div className="p-10 text-center text-slate-400 text-sm">Loading receipt…</div> : (
          <div className={`print-area bg-white text-slate-900 mx-auto ${compact ? "max-w-md p-5" : "p-8"}`}>
            <div className={`flex justify-between items-start border-b-2 border-slate-800 pb-3 mb-4 ${compact ? "flex-col" : ""}`}>
              <div>
                <h1 className={`font-heading font-extrabold uppercase tracking-tight ${compact ? "text-sm" : "text-lg"}`}>{st.company_name || "Prabhat Professional Consultancy Services Pvt. Ltd."}</h1>
                <p className="text-[11px] text-slate-600">Main Office — {st.main_office || "Jamnagar"} · Branch — {st.branch_office || "Vagheshwari Plot, Porbandar"}</p>
              </div>
              <div className={`text-right ${compact ? "text-left mt-2" : ""}`}>
                <div className="inline-block bg-[#0F2942] text-white text-xs font-bold px-3 py-1 rounded">PAYMENT RECEIPT</div>
              </div>
            </div>
            <div className={`grid gap-x-8 gap-y-1.5 text-xs mb-4 ${compact ? "grid-cols-1" : "grid-cols-2"}`}>
              <Row label="Receipt No." value={txn.receipt_number || txn.txn_number} mono />
              <Row label="Date" value={formatDate(txn.txn_date)} />
              <Row label="Candidate" value={cand?.name || txn.candidate_name} />
              <Row label="Candidate ID" value={cand?.candidate_code || txn.candidate_code} mono />
              <Row label="Mobile" value={cand?.mobile || "-"} />
              <Row label="Payment Purpose" value={txn.payment_type || txn.description} />
              <Row label="Payment Mode" value={txn.payment_mode} />
              <Row label="Reference No." value={txn.reference_number || "-"} />
              <Row label="Deposited In" value={txn.account_name} />
              <Row label="Branch" value={txn.branch_name || "-"} />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-emerald-800 uppercase">Amount Received</span>
              <span className="font-mono text-2xl font-extrabold text-emerald-700" data-testid="receipt-amount">{formatINR(amount)}</span>
            </div>
            <p className="text-xs italic text-slate-600 mb-6"><strong>In words:</strong> {numberToWords(amount)}</p>
            {txn.notes && <p className="text-[11px] text-slate-500 mb-6">Note: {txn.notes}</p>}
            <div className={`flex justify-between items-end ${compact ? "mt-8" : "mt-12"}`}>
              <div className="text-xs">
                <div className="text-slate-500">Received By</div>
                <div className="font-semibold mt-1">{txn.collected_by || txn.created_by_name}</div>
              </div>
              <div className="text-xs text-right">
                <div className="border-t border-slate-400 pt-1 w-40 text-center">Authorized Signature</div>
              </div>
            </div>
            <p className="text-center text-[10px] text-slate-400 mt-6">This is a computer-generated receipt.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between border-b border-dashed border-slate-200 py-1">
      <span className="text-slate-500">{label}</span>
      <span className={`font-medium text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
